// Tiny i18n module — no react-i18next, no provider, no codegen. Two
// languages, one persisted preference, browser detection on first visit.
//
// Usage:
//   const lang = detectLang();           // "en" | "de"
//   setLang("de");                       // persists in localStorage
//   pickLang({ en: "Hello", de: "Hallo" }, lang);  // → "Hallo"
//   tt(UI, "back", lang)                 // looks up UI.back[lang]
//
// All user-facing copy lives next to its data: questions in audit.js,
// chrome strings in this file's UI export.

const STORAGE_KEY = "adheran:lang";

export const SUPPORTED = ["en", "de"];

export function detectLang() {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (_) { /* ignore */ }
  const nav = (typeof navigator !== "undefined" && (navigator.language || (navigator.languages && navigator.languages[0]))) || "en";
  return /^de\b/i.test(nav) ? "de" : "en";
}

export function setLang(lang) {
  if (!SUPPORTED.includes(lang)) return;
  try {
    window.localStorage?.setItem(STORAGE_KEY, lang);
  } catch (_) { /* ignore */ }
}

export function pickLang(value, lang) {
  if (value == null) return value;
  if (typeof value === "string") return value;
  if (typeof value === "object" && (value.en !== undefined || value.de !== undefined)) {
    return value[lang] ?? value.en ?? value.de ?? "";
  }
  return value;
}

// Static UI chrome strings used in Assessment.jsx.
export const UI = {
  back: { en: "Back", de: "Zurück" },
  next: { en: "Next", de: "Weiter" },
  see_result: { en: "See my result", de: "Mein Ergebnis ansehen" },
  back_home: { en: "Back to home", de: "Zur Startseite" },
  start_over: { en: "Start over", de: "Von vorn beginnen" },
  question_of: { en: "Question {n} of {total}", de: "Frage {n} von {total}" },
  preliminary: { en: "Preliminary classification", de: "Vorläufige Einstufung" },
  in_force_now: { en: "In force now", de: "Bereits in Kraft" },
  days: { en: "days", de: "Tage" },
  annex_areas_flagged: { en: "Annex III areas you flagged", de: "Anhang-III-Bereiche, die Sie angegeben haben" },
  prohibited_flagged: { en: "Prohibited practices you flagged", de: "Verbotene Praktiken, die Sie angegeben haben" },
  articles_apply: { en: "Articles that apply to you", de: "Für Sie geltende Artikel" },
  carveout_claim: { en: "Article 6(3) exception you're claiming:", de: "Artikel-6(3)-Ausnahme, auf die Sie sich berufen:" },
  carveout_warn: {
    en: "This must be documented and registered before the system goes on the market. The exception is denied if the system performs profiling.",
    de: "Diese Ausnahme muss dokumentiert und vor Markteinführung in der EU-Datenbank registriert werden. Sie entfällt, wenn das System Profiling durchführt.",
  },
  stacked_obligation: { en: "Stacked obligation", de: "Zusätzliche Pflicht" },
  disclaimer_intro: { en: "Preliminary classification — not legal advice.", de: "Vorläufige Einstufung — keine Rechtsberatung." },
  disclaimer_default: {
    en: "This audit applies the AI Act's text to the answers you gave, but a real classification depends on facts an automated tool can't see. Treat this as a starting map, not a verdict. The 25-minute call is where we look at your actual setup.",
    de: "Dieses Audit wendet den Wortlaut der KI-Verordnung auf Ihre Antworten an. Eine belastbare Einstufung hängt jedoch von Details ab, die ein automatisiertes Tool nicht sehen kann. Verstehen Sie das Ergebnis als Wegweiser, nicht als endgültiges Urteil. Im 25-minütigen Gespräch schauen wir uns Ihre tatsächliche Situation an.",
  },
  // Pack preview labels
  what_you_get: { en: "What's included with €299/month", de: "Was Sie für 299 € / Monat erhalten" },
  pack_disclaimer: {
    en: "AI Inventory template is lawyer-reviewed. Other templates: lawyer review in progress, Q3 2026.",
    de: "Die KI-Bestandsliste ist anwaltlich geprüft. Übrige Vorlagen: anwaltliche Prüfung in Arbeit, Q3 2026.",
  },
  vanta_anchor_title: { en: "How this compares", de: "Im Vergleich" },
  vanta_anchor_body: {
    en: "€299/month, cancel anytime, no demo required. Vanta charges $7,500/year minimum for one framework — sales-led, custom quote, 1-year minimum.",
    de: "299 €/Monat, monatlich kündbar, kein Demo-Termin nötig. Vanta verlangt mindestens 7.500 $/Jahr für ein Rahmenwerk — vertrieblich gesteuert, individuelles Angebot, mindestens 1 Jahr Bindung.",
  },
  // CTAs
  start_subscription: { en: "Start your subscription", de: "Abonnement starten" },
  book_call: { en: "Book the 25-min call", de: "25-Minuten-Termin buchen" },
  book_call_alt: { en: "Or book the 25-min call", de: "Oder einen 25-Minuten-Termin buchen" },
  email_audit: { en: "Email me the audit", de: "Audit per E-Mail zusenden" },
  cta_strong_title_high: { en: "Worth a 25-minute call before August", de: "Ein 25-minütiges Gespräch lohnt sich vor August" },
  cta_strong_body_high: {
    en: "I'll review your answers, walk through what Annex III actually demands of you, and tell you honestly whether Adheran can help — or whether your situation needs a lawyer first.",
    de: "Ich gehe Ihre Antworten durch, erkläre Ihnen, was Anhang III konkret von Ihnen verlangt, und sage ehrlich, ob Adheran weiterhilft — oder ob Ihre Situation zuerst einen Anwalt braucht.",
  },
  cta_soft_title: { en: "Want me to email you the full audit + a one-pager?", de: "Soll ich Ihnen das vollständige Audit per E-Mail schicken?" },
  cta_soft_body: {
    en: "Drop your email and I'll send a written version of this result you can forward to your CTO or lawyer.",
    de: "Hinterlassen Sie Ihre E-Mail-Adresse, und ich schicke Ihnen eine schriftliche Version dieses Ergebnisses zum Weiterleiten an CTO oder Anwalt.",
  },
  cta_prohibited_title: { en: "Talk to a lawyer this week", de: "Sprechen Sie diese Woche mit einem Anwalt" },
  cta_prohibited_body: {
    en: "I can't help you compliance-engineer a system Article 5 prohibits — no one can. If the audit is wrong about what your system does, I'd want to hear that on a call. If it's right, the next 30 minutes should be spent finding qualified counsel.",
    de: "Ein durch Artikel 5 verbotenes System lässt sich nicht compliance-konform nachrüsten — von niemandem. Sollte das Audit Ihre Lösung falsch eingestuft haben, hören wir das gerne in einem Gespräch. Wenn es richtig liegt, sollten die nächsten 30 Minuten der Suche nach einer qualifizierten Kanzlei gehören.",
  },
  // Form
  form_name: { en: "Name", de: "Name" },
  form_email: { en: "Work email", de: "Geschäftliche E-Mail" },
  form_company: { en: "Company", de: "Unternehmen" },
  form_role: { en: "Your role", de: "Ihre Rolle" },
  form_role_ph: { en: "e.g. Founder, Head of People", de: "z. B. Gründer:in, Head of People" },
  form_send: { en: "Email me the audit", de: "Audit per E-Mail zusenden" },
  form_sending: { en: "Sending…", de: "Wird gesendet…" },
  form_invalid_email: { en: "Please enter a valid work email.", de: "Bitte geben Sie eine gültige geschäftliche E-Mail-Adresse ein." },
  form_privacy: {
    en: "Your answers stay between us. EU-hosted, GDPR-aligned. No newsletter spam.",
    de: "Ihre Antworten bleiben zwischen uns. EU-gehostet, DSGVO-konform. Kein Newsletter-Spam.",
  },
  // Success
  success_thanks: { en: "Got it — thanks, {name}.", de: "Alles klar — danke, {name}." },
  success_body: {
    en: "I'll be in touch within one business day from {email}. If you want to skip the wait, the calendar is open below.",
    de: "Ich melde mich innerhalb eines Werktags von {email} aus. Wer nicht warten möchte, findet den Kalender unten.",
  },
  success_book_now: { en: "Book a 25-min call now", de: "Jetzt einen 25-Minuten-Termin buchen" },
  // Beta DE notice
  de_beta_notice: {
    en: "",
    de: "Hinweis: Die deutsche Fassung wird derzeit von einem Muttersprachler überprüft. Bei Unklarheiten gilt das englische Original.",
  },
};

export function tt(table, key, lang, params = {}) {
  const entry = table?.[key];
  let out = pickLang(entry, lang) || "";
  if (params && typeof out === "string") {
    for (const [k, v] of Object.entries(params)) {
      out = out.replaceAll(`{${k}}`, String(v));
    }
  }
  return out;
}
